import React, { createContext, Dispatch, PropsWithChildren, useContext, useEffect, useReducer } from 'react';
import { getColor, getItemFromStore } from 'helpers/utils';
import { Config, initialConfig, SupportedLanguage, ThemeVariant } from 'config';
import { ACTIONTYPE, configReducer, SET_CONFIG } from 'reducers/ConfigReducer';
import { useTranslation } from 'react-i18next';
import { ApiClient } from 'helpers/api-client';
import { Bounce, toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamation } from '@fortawesome/free-solid-svg-icons';

interface AppContextInterFace {
  config: Config;
  configDispatch: Dispatch<ACTIONTYPE>;
  toggleTheme: () => void;
  selectTheme: (theme: ThemeVariant) => void;
  selectLanguage: (language: SupportedLanguage) => void;
  setConfig: (payload: Partial<Config>) => void;
  getThemeColor: (name: string) => string;
  setAuthorization: (token: string | null) => void;
  setCurrentProjectId: (projectId: number | null) => void;
  setLastBillingTab: (tab: 'Invoices' | 'Offers') => void;
  successNotification: (message: string) => void;
  errorNotification: (message: string) => void;
  apiClient: ApiClient;
}

export const AppContext = createContext({} as AppContextInterFace);

const AppProvider = ({ children }: PropsWithChildren) => {
  const { i18n } = useTranslation();

  const getInitialCurrentProjectId = (): number | null => {
    const stored = getItemFromStore('currentProjectId');
    return stored !== undefined && stored !== null && stored !== 'null' ? Number(stored) : null;
  };

  const configState: Config = {
    openNavbarVertical: initialConfig.openNavbarVertical,
    isNavbarVerticalCollapsed: getItemFromStore('isNavbarVerticalCollapsed', initialConfig.isNavbarVerticalCollapsed),
    theme: getItemFromStore('theme', initialConfig.theme),
    navbarTopAppearance: getItemFromStore('navbarTopAppearance', initialConfig.navbarTopAppearance),
    navbarVerticalAppearance: getItemFromStore('navbarVerticalAppearance', initialConfig.navbarVerticalAppearance),
    navbarPosition: getItemFromStore('navbarPosition', initialConfig.navbarPosition),
    navbarTopShape: getItemFromStore('navbarTopShape', initialConfig.navbarTopShape),
    isRTL: getItemFromStore('isRTL', initialConfig.isRTL),
    isDark: getItemFromStore('isDark', initialConfig.isDark),
    isChatWidgetVisible: getItemFromStore('isChatWidgetVisible', initialConfig.isChatWidgetVisible),
    language: getItemFromStore('language', initialConfig.language),
    authorization: getItemFromStore('authorization', initialConfig.authorization || ''),
    currentProjectId: getInitialCurrentProjectId(),
    lastBillingTab: getItemFromStore('lastBillingTab', initialConfig.lastBillingTab),
    taskListFormat: getItemFromStore('taskListFormat', initialConfig.taskListFormat),
    hourCardListFormat: getItemFromStore('hourCardListFormat', initialConfig.hourCardListFormat)
  };

  const [config, configDispatch] = useReducer(configReducer, configState); // initail

  const setConfig = (payload: Partial<Config>) => {
    configDispatch({
      type: SET_CONFIG,
      payload
    });
  };

  const toggleTheme = () => {
    configDispatch({
      type: SET_CONFIG,
      payload: {
        theme: config.isDark ? 'light' : 'dark'
      }
    });
  };

  const selectTheme = (theme: ThemeVariant) => {
    configDispatch({
      type: SET_CONFIG,
      payload: {
        theme: theme
      }
    });
  };

  const selectLanguage = (language: SupportedLanguage) => {
    configDispatch({
      type: SET_CONFIG,
      payload: {
        language: language
      }
    });
    i18n.changeLanguage(language);
  };

  const getThemeColor = (name: string) => {
    return getColor(name);
  };

  const setAuthorization = (token: string | null) => {
    configDispatch({
      type: SET_CONFIG,
      payload: {
        authorization: token
      }
    });
  };

  const setCurrentProjectId = (projectId: number | null) => {
    configDispatch({
      type: SET_CONFIG,
      payload: {
        currentProjectId: projectId
      }
    });
  };

  const setLastBillingTab = (tab: 'Invoices' | 'Offers') => {
    configDispatch({
      type: SET_CONFIG,
      payload: {
        lastBillingTab: tab
      }
    });
  };

  const getApiClient = (): ApiClient => {
    return new ApiClient(
      process.env.REACT_APP_API_URL || '',
      i18n.language as SupportedLanguage,
      i18n.t('apiError'),
      config.authorization
    );
  };

  // Styled by the .Toastify__toast rules in _app.scss: mono caption over the message, icon tile in the status colour
  const notify = (type: 'success' | 'error', message: string) => {
    toast[type](
      <div>
        <div className="so-label">{i18n.t(type === 'success' ? 'notificationSuccess' : 'notificationError')}</div>
        <div className="so-toast-message">{message}</div>
      </div>,
      {
        position: 'top-center',
        autoClose: 4000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: config.isDark ? 'dark' : 'light',
        transition: Bounce,
        icon: <FontAwesomeIcon icon={type === 'success' ? faCheck : faExclamation} />
      }
    );
  };

  const successNotification = (message: string) => notify('success', message);

  const errorNotification = (message: string) => notify('error', message);

  useEffect(() => {
    if (config.navbarTopShape === 'slim') {
      // document.body.classList.add('nav-slim');
      document.documentElement.setAttribute('data-navbar-horizontal-shape', 'slim');
    } else {
      document.documentElement.removeAttribute('data-navbar-horizontal-shape');
    }

    if (config.navbarPosition === 'dual') {
      setConfig({
        navbarTopShape: 'default'
      });
    }

    document.documentElement.setAttribute('data-navigation-type', config.navbarPosition);

    if (config.isNavbarVerticalCollapsed) {
      document.documentElement.classList.add('navbar-vertical-collapsed');
    } else {
      document.documentElement.classList.remove('navbar-vertical-collapsed');
    }

    if (config.language !== i18n.language) {
      i18n.changeLanguage(config.language);
    }

    document.documentElement.setAttribute('lang', config.language);
  }, [config]);

  return (
    <AppContext.Provider
      value={{
        config,
        setConfig,
        toggleTheme,
        selectTheme,
        selectLanguage,
        getThemeColor,
        configDispatch,
        setAuthorization,
        setCurrentProjectId,
        setLastBillingTab,
        successNotification,
        errorNotification,
        apiClient: getApiClient()
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);

export default AppProvider;
